import React, { useState } from "react";
import { Table } from "react-bootstrap";

interface PasswordSearchResultsProps {
  students: any[]; // studentList from main search results
}

const PasswordSearchResults: React.FC<PasswordSearchResultsProps> = ({ students }) => {
  const [visiblePasswords, setVisiblePasswords] = useState<Record<string, boolean>>({});
  const [copiedKey, setCopiedKey] = useState<string | null>(null);

  const togglePasswordVisibility = (key: string) => {
    setVisiblePasswords((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const flashCopied = (key: string) => {
    setCopiedKey(key);
    window.setTimeout(() => {
      setCopiedKey((curr) => (curr === key ? null : curr));
    }, 1200);
  };

  const fallbackCopy = (text: string): boolean => {
    try {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.style.position = "fixed";
      ta.style.top = "-9999px";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      const ok = document.execCommand("copy");
      document.body.removeChild(ta);
      return ok;
    } catch (err) {
      console.error("Fallback copy failed:", err);
      return false;
    }
  };

  const copyToClipboard = (text: string, key: string) => {
    if (!text) return;
    const onSuccess = () => flashCopied(key);
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(text).then(onSuccess, (err) => {
        console.error("Clipboard API failed, falling back:", err);
        if (fallbackCopy(text)) onSuccess();
      });
    } else if (fallbackCopy(text)) {
      onSuccess();
    }
  };

  if (!students || students.length === 0) {
    return (
      <div style={{ padding: 30, textAlign: "center", color: "var(--text-faint)" }}>
        No results found
      </div>
    );
  }

  return (
    <div style={{ overflowX: "auto", background: "var(--surface-card)", borderRadius: 4 }}>
      <Table striped bordered hover style={{ marginBottom: 0, fontSize: 13 }}>
        <thead style={{ background: "var(--status-info)", color: "var(--text-on-brand)" }}>
          <tr>
            <th style={{ padding: "10px 12px" }}>Enquiry ID</th>
            <th style={{ padding: "10px 12px" }}>Student Name</th>
            <th style={{ padding: "10px 12px" }}>Student Code</th>
            <th style={{ padding: "10px 12px" }}>Student Username</th>
            <th style={{ padding: "10px 12px" }}>Student Password</th>
            <th style={{ padding: "10px 12px" }}>Parent Username</th>
            <th style={{ padding: "10px 12px" }}>Parent Password</th>
          </tr>
        </thead>
        <tbody>
          {students.map((student, index) => {
            const studentPwdKey = `student-${student.enquiryId}`;
            const parentPwdKey = `parent-${student.enquiryId}`;
            const studentUserKey = `student-user-${student.enquiryId}`;
            const studentPwdCopyKey = `student-pwd-${student.enquiryId}`;
            const parentUserKey = `parent-user-${student.enquiryId}`;
            const parentPwdCopyKey = `parent-pwd-${student.enquiryId}`;
            return (
              <tr key={index}>
                <td style={{ padding: "8px 12px" }}>{student.enquiryId}</td>
                <td style={{ padding: "8px 12px" }}>{student.name || "-"}</td>
                <td style={{ padding: "8px 12px" }}>{student.studentCode || "-"}</td>
                <td style={{ padding: "8px 12px" }}>
                  {student.studentUserName || "-"}
                  {student.studentUserName && (
                    <button
                      type="button"
                      onClick={() => copyToClipboard(student.studentUserName, studentUserKey)}
                      style={{
                        marginLeft: 6,
                        padding: "2px 6px",
                        fontSize: 11,
                        border: "none",
                        background: copiedKey === studentUserKey ? "#d4edda" : "#e9ecef",
                        color: copiedKey === studentUserKey ? "#155724" : "inherit",
                        borderRadius: 3,
                        cursor: "pointer",
                      }}
                      title="Copy"
                    >
                      {copiedKey === studentUserKey ? "Copied!" : <i className="fa fa-copy" />}
                    </button>
                  )}
                </td>
                <td style={{ padding: "8px 12px" }}>
                  {student.studentPassword ? (
                    <>
                      <span style={{ fontFamily: "monospace", marginRight: 6 }}>
                        {visiblePasswords[studentPwdKey]
                          ? student.studentPassword
                          : "••••••••"}
                      </span>
                      <button
                        onClick={() => togglePasswordVisibility(studentPwdKey)}
                        style={{
                          marginRight: 4,
                          padding: "2px 6px",
                          fontSize: 11,
                          border: "none",
                          background: "var(--surface-sunken)",
                          borderRadius: 3,
                          cursor: "pointer",
                        }}
                        title={visiblePasswords[studentPwdKey] ? "Hide" : "Show"}
                      >
                        <i className={`fa fa-eye${visiblePasswords[studentPwdKey] ? "-slash" : ""}`} />
                      </button>
                      <button
                        type="button"
                        onClick={() => copyToClipboard(student.studentPassword, studentPwdCopyKey)}
                        style={{
                          padding: "2px 6px",
                          fontSize: 11,
                          border: "none",
                          background: copiedKey === studentPwdCopyKey ? "#d4edda" : "#e9ecef",
                          color: copiedKey === studentPwdCopyKey ? "#155724" : "inherit",
                          borderRadius: 3,
                          cursor: "pointer",
                        }}
                        title="Copy"
                      >
                        {copiedKey === studentPwdCopyKey ? "Copied!" : <i className="fa fa-copy" />}
                      </button>
                    </>
                  ) : (
                    "-"
                  )}
                </td>
                <td style={{ padding: "8px 12px" }}>
                  {student.parentUserName || "-"}
                  {student.parentUserName && (
                    <button
                      type="button"
                      onClick={() => copyToClipboard(student.parentUserName, parentUserKey)}
                      style={{
                        marginLeft: 6,
                        padding: "2px 6px",
                        fontSize: 11,
                        border: "none",
                        background: copiedKey === parentUserKey ? "#d4edda" : "#e9ecef",
                        color: copiedKey === parentUserKey ? "#155724" : "inherit",
                        borderRadius: 3,
                        cursor: "pointer",
                      }}
                      title="Copy"
                    >
                      {copiedKey === parentUserKey ? "Copied!" : <i className="fa fa-copy" />}
                    </button>
                  )}
                </td>
                <td style={{ padding: "8px 12px" }}>
                  {student.parentPassword ? (
                    <>
                      <span style={{ fontFamily: "monospace", marginRight: 6 }}>
                        {visiblePasswords[parentPwdKey]
                          ? student.parentPassword
                          : "••••••••"}
                      </span>
                      <button
                        onClick={() => togglePasswordVisibility(parentPwdKey)}
                        style={{
                          marginRight: 4,
                          padding: "2px 6px",
                          fontSize: 11,
                          border: "none",
                          background: "var(--surface-sunken)",
                          borderRadius: 3,
                          cursor: "pointer",
                        }}
                        title={visiblePasswords[parentPwdKey] ? "Hide" : "Show"}
                      >
                        <i className={`fa fa-eye${visiblePasswords[parentPwdKey] ? "-slash" : ""}`} />
                      </button>
                      <button
                        type="button"
                        onClick={() => copyToClipboard(student.parentPassword, parentPwdCopyKey)}
                        style={{
                          padding: "2px 6px",
                          fontSize: 11,
                          border: "none",
                          background: copiedKey === parentPwdCopyKey ? "#d4edda" : "#e9ecef",
                          color: copiedKey === parentPwdCopyKey ? "#155724" : "inherit",
                          borderRadius: 3,
                          cursor: "pointer",
                        }}
                        title="Copy"
                      >
                        {copiedKey === parentPwdCopyKey ? "Copied!" : <i className="fa fa-copy" />}
                      </button>
                    </>
                  ) : (
                    "-"
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    </div>
  );
};

export default PasswordSearchResults;
